import { DeesElement, html, customElement, type TemplateResult, property, state, cssManager, css } from '@design.estate/dees-element';
import * as plugins from '../../00plugins.js';
import { demoFunc } from './dees-dataview-statusobject.demo.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-layout/dees-tile/dees-tile.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap { 'dees-dataview-statusobject': DeesDataviewStatusobject; }
}

type TDetail = plugins.tsclass.code.IStatusObject['details'][number];
const statusPresentation = {
  ok: { label: 'Healthy', icon: 'lucide:CircleCheck' },
  partly_ok: { label: 'Needs attention', icon: 'lucide:TriangleAlert' },
  not_ok: { label: 'Unavailable', icon: 'lucide:CircleX' },
};

@customElement('dees-dataview-statusobject')
export class DeesDataviewStatusobject extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Object }) accessor statusObject!: plugins.tsclass.code.IStatusObject;
  /** Drop the outer frame: the host already owns the surface. */
  @property({ type: Boolean, reflect: true }) accessor frameless = false;
  @state() accessor copied = false;
  @state() private accessor copyFeedback = '';
  @state() private accessor copyFailed = false;
  private copiedResetTimeout?: ReturnType<typeof setTimeout>;
  private copyRequest = 0;

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: block; min-width: 0; container: dees-status / inline-size; font: 13px/1.5 var(--dees-font-family); color: var(--dees-color-text-primary); }
    dees-tile::part(outer) { border-color: var(--dees-color-border-subtle); }
    .heading { display: flex; align-items: flex-start; gap: 12px; padding: 18px 16px; }
    .heading-text { flex: 1; min-width: 0; }
    h2 { margin: 0; font-size: 16px; line-height: 1.4; font-weight: 600; letter-spacing: -.02em; overflow-wrap: anywhere; }
    .summary { display: flex; align-items: flex-start; gap: 6px; margin-top: 5px; font-size: 12px; color: var(--dees-color-text-secondary); }
    .summary dees-icon { margin-top: 2px; flex: 0 0 auto; }
    .ok { color: var(--dees-color-text-success); }
    .partly_ok { color: var(--dees-color-text-warning); }
    .not_ok { color: var(--dees-color-text-error); }
    .unknown { color: var(--dees-color-text-muted); }
    .control { display: inline-flex; justify-content: center; align-items: center; gap: 6px; flex: 0 0 auto; box-sizing: border-box; min-height: 28px; border: 0; border-radius: var(--dees-radius-md); padding: 0 8px; color: var(--dees-color-text-secondary); background: transparent; font: 12px var(--dees-font-family); cursor: pointer; }
    .copyMain { min-height: 32px; padding: 0 10px; border: 1px solid var(--dees-color-border-default); background: var(--dees-color-control-bg); }
    .control:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: 2px; }
    .control:active:not(:disabled) { background: var(--dees-color-active); }
    .control:disabled { opacity: .45; cursor: default; }
    @media (hover: hover) { .control:hover:not(:disabled) { background: var(--dees-color-hover); color: var(--dees-color-text-primary); } }
    .details { margin: 0; padding: 0; list-style: none; }
    .detail { display: grid; grid-template-columns: 18px minmax(0, 1fr) 28px; gap: 12px; align-items: start; padding: 14px 16px; border-bottom: 1px solid var(--dees-color-border-subtle); }
    .detail:last-child { border-bottom: 0; }
    .detail > dees-icon { margin-top: 3px; }
    .detailsText { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: baseline; column-gap: 16px; min-width: 0; margin: 0; }
    .label { font-size: 13px; font-weight: 500; color: var(--dees-color-text-primary); overflow-wrap: anywhere; }
    .value { margin: 0; text-align: end; font-size: 13px; font-variant-numeric: tabular-nums; color: var(--dees-color-text-primary); overflow-wrap: anywhere; min-width: 0; }
    .explanation { grid-column: 1 / -1; margin: 3px 0 0; color: var(--dees-color-text-muted); font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
    .detail-action { width: 28px; padding: 0; margin-top: -4px; }
    .bottomBar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 4px 12px; min-height: 36px; padding: 8px 16px; box-sizing: border-box; font-size: 11px; color: var(--dees-color-text-muted); }
    .feedback { min-width: 0; overflow-wrap: anywhere; }
    .feedback.error { color: var(--dees-color-text-error); }
    .empty { padding: 28px 16px; color: var(--dees-color-text-muted); font-size: 13px; }
    @media (pointer: coarse) {
      .control { min-width: 44px; min-height: 44px; }
      .detail { grid-template-columns: 18px minmax(0, 1fr) 44px; gap: 10px; }
      .detail-action { margin-top: -10px; }
    }
    @container dees-status (max-width: 480px) {
      .heading { gap: 8px; }
      .copyMain { padding-inline: 8px; }
      .copyMain span { display: none; }
      .detailsText { grid-template-columns: minmax(0, 1fr); gap: 3px; }
      .value { text-align: start; }
    }
  `];

  public render(): TemplateResult {
    const status = this.statusObject;
    const presentation = status?.combinedStatus ? statusPresentation[status.combinedStatus] : undefined;
    const timestamp = status?.lastUpdated === undefined ? undefined : new Date(status.lastUpdated);
    const validTimestamp = timestamp && Number.isFinite(timestamp.getTime());
    return html`<dees-tile .frameless=${this.frameless}>
      <div slot="header" class="heading">
        <div class="heading-text"><h2>${status?.name || 'Service status'}</h2>
          <div class="summary ${status?.combinedStatus || 'unknown'}">
            <dees-icon aria-hidden="true" .icon=${presentation?.icon || 'lucide:CircleHelp'} iconSize="14"></dees-icon>
            <span>${status?.combinedStatusText || presentation?.label || 'Status not reported'}</span>
          </div>
        </div>
        <button type="button" class="control copyMain" aria-label=${this.copied ? 'Copied status JSON' : 'Copy status as JSON'}
          ?disabled=${!status} @click=${this.handleCopyAsJson}>
          <dees-icon aria-hidden="true" .icon=${this.copied ? 'lucide:Check' : 'lucide:Copy'} iconSize="14"></dees-icon>
          <span>${this.copied ? 'Copied' : 'Copy JSON'}</span>
        </button>
      </div>
      ${status?.details?.length ? html`<ul class="details">
        ${status.details.map((detail, index) => {
          const state = statusPresentation[detail.status];
          return html`<li class="detail" @contextmenu=${(event: MouseEvent) => this.openDetailMenu(event, detail)}>
            <dees-icon class=${detail.status} aria-hidden="true" .icon=${state?.icon || 'lucide:CircleHelp'} iconSize="16"></dees-icon>
            <dl class="detailsText"><dt class="label">${detail.name}</dt><dd class="value">${detail.value}</dd>
              <dd class="explanation">${detail.statusText || state?.label || 'Status not reported'}</dd>
            </dl>
            <button type="button" class="control detail-action" aria-label=${`Copy options for ${detail.name || `detail ${index + 1}`}`} aria-haspopup="menu"
              @click=${this.openControlMenu} @keydown=${(event: KeyboardEvent) => {
                if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) this.openControlMenu(event);
              }}><dees-icon aria-hidden="true" icon="lucide:Ellipsis" iconSize="16"></dees-icon></button>
          </li>`;
        })}
      </ul>` : html`<div class="empty">${status ? 'No detail checks reported.' : 'Connect a service to see its health and checks.'}</div>`}
      <div slot="footer" class="bottomBar">
        <span>${validTimestamp ? html`Updated <time datetime=${timestamp.toISOString()}>${timestamp.toLocaleString()}</time>` : 'No update time reported'}</span>
        <span class="feedback ${this.copyFailed ? 'error' : ''}" role="status" aria-live="polite">${this.copyFeedback}</span>
      </div>
    </dees-tile>`;
  }

  private openControlMenu(event: MouseEvent | KeyboardEvent) {
    event.preventDefault(); event.stopPropagation();
    const control = event.currentTarget as HTMLElement;
    control.focus({ preventScroll: true });
    const rect = control.getBoundingClientRect();
    control.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, composed: true, cancelable: true, clientX: rect.left, clientY: rect.bottom }));
  }

  private openDetailMenu(event: MouseEvent, detail: TDetail) {
    void DeesContextmenu.openContextMenuWithOptions(event, [
      { name: 'Copy value', iconName: 'lucide:Copy', action: async () => this.copyToClipboard(detail.value, `${detail.name} value copied`) },
      { name: 'Copy key', iconName: 'lucide:Key', action: async () => this.copyToClipboard(detail.name, `${detail.name} key copied`) },
      { name: 'Copy key and value', iconName: 'lucide:CopyPlus', action: async () => this.copyToClipboard(`${detail.name}: ${detail.value}`, `${detail.name} key and value copied`) },
    ]);
  }

  private async copyToClipboard(text: string, success: string, json = false) {
    const request = ++this.copyRequest;
    clearTimeout(this.copiedResetTimeout);
    this.copied = false;
    this.copyFeedback = '';
    try {
      await navigator.clipboard.writeText(text);
      if (!this.isConnected || request !== this.copyRequest) return;
      this.copied = json;
      this.copyFailed = false;
      this.copyFeedback = success;
    } catch {
      if (!this.isConnected || request !== this.copyRequest) return;
      this.copyFailed = true;
      this.copyFeedback = 'Could not copy. Check clipboard access and try again.';
    }
    this.copiedResetTimeout = setTimeout(() => {
      this.copied = false; this.copyFeedback = ''; this.copyFailed = false;
      this.copiedResetTimeout = undefined;
    }, 3000);
  }

  private handleCopyAsJson() {
    if (this.statusObject) void this.copyToClipboard(JSON.stringify(this.statusObject, null, 2), 'Status JSON copied', true);
  }

  public async disconnectedCallback() {
    ++this.copyRequest;
    clearTimeout(this.copiedResetTimeout);
    this.copiedResetTimeout = undefined;
    this.copied = false; this.copyFeedback = ''; this.copyFailed = false;
    await super.disconnectedCallback();
  }
}
